import { useState, useEffect, useMemo } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { isCommercialModeEnabled } from "@/utils/featureFlags";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RichTextarea } from "@/components/ui/RichTextarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, Trash2, Megaphone, PanelLeft, RectangleHorizontal, AppWindow, 
  Edit2, Upload, Briefcase, CreditCard, Banknote, CheckCircle, XCircle 
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import { useSearchParams } from "react-router-dom";

export type AdDisplayMode = "sidebar" | "banner" | "popup" | "section_title_left" | "section_title_right";
export type PopupTrigger = "homepage" | "first_click" | "always" | "specific_page";
export type ImagePosition = "top" | "bottom" | "left" | "right" | "background";

export type Ad = {
  id: string;
  internalName?: string;
  titleFr: string; titleAr: string; titleZgh: string;
  descriptionFr: string; descriptionAr: string; descriptionZgh: string;
  linkUrl: string; bgColor: string; active: boolean; displayMode: AdDisplayMode;
  imageUrl?: string; imagePosition?: ImagePosition;
  isClosable?: boolean; targetPage?: string; popupTrigger?: PopupTrigger;
  targetSection?: string;
  publishStatus?: "draft" | "published";
  startDate?: string;
  endDate?: string;
  popupSize?: "sm" | "md" | "lg" | "xl";
  textFormat?: "normal" | "bold" | "italic" | "bold-italic";
};

export type PendingAd = {
  id: string; title: string; linkUrl: string; imageUrl: string;
  duration: string; location: string; cost: number;
  organizerName: string; organizerId: string; submittedAt: string;
  status: "pending" | "approved" | "rejected";
  paymentStatus: "paid" | "unpaid";
  isModified?: boolean;
  updatedAt?: string;
};

const colorOptions = [
  { value: "bg-primary", label: "Bleu Principal (Primary)" },
  { value: "bg-secondary", label: "Orange (Secondary)" },
  { value: "bg-accent", label: "Doré (Accent)" },
  { value: "bg-destructive", label: "Rouge Alerte (Destructive)" },
  { value: "bg-background", label: "Transparent / Blanc" },
];

const AdminAdsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang } = useI18n();
  const { toast } = useToast();

  // State: System Ads
  const [ads, setAds] = useState<Ad[]>([]);
  // State: Pending Partner Ads
  const [pendingAds, setPendingAds] = useState<PendingAd[]>([]);
  const [commercialModeOn, setCommercialModeOn] = useState(isCommercialModeEnabled());

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");

  const fetchAds = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || "https://kaynin.ma/backend/api";
      const res = await fetch(`${API_URL}/ads.php?type=all`);
      if (res.ok) {
        const data = await res.json();
        setAds(data.system_ads || []);
        setPendingAds(data.partner_ads || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAds();
    const handleModeChange = (e: any) => setCommercialModeOn(e.detail.enabled);
    window.addEventListener("commercialModeChanged", handleModeChange);
    return () => window.removeEventListener("commercialModeChanged", handleModeChange);
  }, []);

  // Mock Balances (Usually from database)
  const [partnerBalances, setPartnerBalances] = useState<Record<string, number>>({
    "RNI": 5000,
    "PAM": 3500,
    "PI": 2000,
    "USFP": 150,
  });

  // Form states 
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const initialForm: Omit<Ad, "id" | "active"> = {
    internalName: "",
    titleFr: "", titleAr: "", titleZgh: "", descriptionFr: "", descriptionAr: "", descriptionZgh: "",
    imagePosition: "left", isClosable: true, targetPage: "", targetSection: "parties",
    startDate: "", endDate: "", popupSize: "md", textFormat: "normal",
    displayMode: "popup", bgColor: "bg-primary", linkUrl: ""
  };
  const [form, setForm] = useState(initialForm);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Stats Computations
  const totalRevenues = useMemo(() => {
    return pendingAds.filter(a => a.paymentStatus === "paid").reduce((acc, curr) => acc + curr.cost, 0);
  }, [pendingAds]);

  const activePartnerAdsCount = pendingAds.filter(a => a.status === "approved").length;

  const filteredPendingAds = useMemo(() => {
    return pendingAds.filter(pad => {
      if (statusFilter !== "all" && pad.status !== statusFilter) return false;
      if (partnerFilter !== "all" && pad.organizerName !== partnerFilter) return false;
      if (monthFilter !== "all") {
        const d = new Date(pad.submittedAt);
        if (`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` !== monthFilter) return false;
      }
      return true;
    });
  }, [pendingAds, statusFilter, partnerFilter, monthFilter]);

  const exportToCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,\uFEFF"; // Add BOM for Excel
    csvContent += "Partenaire;Titre;Duree;Lieu;Cout;Statut;Paiement;Soumission;Modifie\n";
    filteredPendingAds.forEach(pad => {
      const row = [
        `"${pad.organizerName}"`, `"${pad.title}"`, pad.duration, `"${pad.location}"`, 
        pad.cost, pad.status, pad.paymentStatus, `"${pad.submittedAt}"`, pad.isModified ? "Oui" : "Non"
      ].join(";");
      csvContent += row + "\n";
    });
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `pubs_partenaires_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Actions for Internal Ads
  const saveAd = async (status: "draft" | "published") => {
    if (!form.titleFr.trim() && !form.imageUrl?.trim()) return;
    
    const adToSave = editingId 
      ? { ...(ads.find(a => a.id === editingId) || {} as Ad), ...form, publishStatus: status }
      : { ...form, id: Date.now().toString(), active: true, publishStatus: status };

    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const API_URL = import.meta.env.VITE_API_URL || "https://kaynin.ma/backend/api";
      const res = await fetch(`${API_URL}/ads.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { "Authorization": `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action: "save_system_ad", ad: adToSave })
      });
      if (res.ok) {
        fetchAds();
        setForm(initialForm); setEditingId(null); setShowForm(false);
      }
    } catch(e) {}
  };

  const handleEditClick = (ad: Ad) => {
    setEditingId(ad.id);
    setForm({
      internalName: ad.internalName || "",
      titleFr: ad.titleFr, titleAr: ad.titleAr || "", titleZgh: ad.titleZgh || "",
      descriptionFr: ad.descriptionFr || "", descriptionAr: ad.descriptionAr || "", descriptionZgh: ad.descriptionZgh || "",
      linkUrl: ad.linkUrl || "", bgColor: ad.bgColor, displayMode: ad.displayMode,
      imageUrl: ad.imageUrl || "", imagePosition: ad.imagePosition || "left",
      isClosable: ad.isClosable !== false, targetPage: ad.targetPage || "", popupTrigger: ad.popupTrigger || "homepage",
      targetSection: ad.targetSection || "parties",
      startDate: ad.startDate || "", endDate: ad.endDate || "",
      popupSize: ad.popupSize || "md", textFormat: ad.textFormat || "normal"
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id: string) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const API_URL = import.meta.env.VITE_API_URL || "https://kaynin.ma/backend/api";
      const res = await fetch(`${API_URL}/ads.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { "Authorization": `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action: "delete_system_ad", id })
      });
      if (res.ok) fetchAds();
    } catch(e) {}
  };

  const toggleActive = async (id: string) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const API_URL = import.meta.env.VITE_API_URL || "https://kaynin.ma/backend/api";
      const res = await fetch(`${API_URL}/ads.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { "Authorization": `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action: "toggle_system_ad", id })
      });
      if (res.ok) fetchAds();
    } catch(e) {}
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setForm({ ...form, imageUrl: event.target?.result as string });
    };
    reader.readAsDataURL(file);
  };

  // Actions for Partner Ads Validation
  const handleApproveAd = async (pAd: PendingAd) => {
    const mockKey = Object.keys(partnerBalances).find(k => pAd.organizerName.includes(k)) || "Generic";
    const currentBalance = partnerBalances[mockKey] || 5000; 

    if (currentBalance < pAd.cost) {
      toast({ title: "Solde Insuffisant", description: `Le partenaire (${mockKey}) n'a pas les fonds nécessaires (${pAd.cost} MAD).`, variant: "destructive" });
      return;
    }

    setPartnerBalances(prev => ({ ...prev, [mockKey]: currentBalance - pAd.cost }));

    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const API_URL = import.meta.env.VITE_API_URL || "https://kaynin.ma/backend/api";
      await fetch(`${API_URL}/ads.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { "Authorization": `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action: "moderate_partner_ad", id: pAd.id, status: "approved", paymentStatus: "paid" })
      });

      const newOfficialAd: Ad = {
        id: `sys_${Date.now()}`,
        titleFr: pAd.title, titleAr: "", titleZgh: "",
        descriptionFr: `Campagne sponsorisée par ${pAd.organizerName}.`, descriptionAr: "", descriptionZgh: "",
        linkUrl: pAd.linkUrl, bgColor: "bg-primary", active: true,
        displayMode: pAd.location === "homepage" ? "banner" : "popup",
        imageUrl: pAd.imageUrl, isClosable: true,
        popupTrigger: pAd.location === "homepage" ? "homepage" : "always"
      };

      await fetch(`${API_URL}/ads.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { "Authorization": `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action: "save_system_ad", ad: newOfficialAd })
      });

      fetchAds();
      toast({ title: "Publicité Validée", description: `Le montant de ${pAd.cost} MAD a été déduit avec succès. L'annonce est publiée.` });
    } catch(e) {}
  };

  const handleRejectAd = async (id: string) => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      const API_URL = import.meta.env.VITE_API_URL || "https://kaynin.ma/backend/api";
      await fetch(`${API_URL}/ads.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { "Authorization": `Bearer ${token}` } : {}) },
        body: JSON.stringify({ action: "moderate_partner_ad", id, status: "rejected", paymentStatus: "unpaid" })
      });
      fetchAds();
      toast({ title: "Publicité Rejetée", description: "L'annonce ne sera pas diffusée.", variant: "destructive" });
    } catch(e) {}
  };


  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-6xl mx-auto pb-10">
      <div className="flex flex-col gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold text-foreground flex items-center gap-3">
            <Megaphone className="w-7 h-7 md:w-8 md:h-8 text-primary" />
            Gestion des Publicités &amp; Finances
          </h1>
          <p className="text-muted-foreground font-body mt-1 text-sm">
            Gérez les campagnes internes, modérez les propositions partenaires et suivez la facturation.
          </p>
        </div>
      </div>

      <Tabs value={searchParams.get("tab") || "partners"} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
        <TabsList className="mb-6 h-auto bg-muted/50 p-1 flex flex-wrap rounded-xl w-full max-w-2xl gap-1">
          <TabsTrigger value="partners" className="rounded-lg flex-1 min-w-[8rem] font-semibold flex items-center gap-2 text-xs md:text-sm">
            <Briefcase className="w-4 h-4" /> <span className="hidden sm:inline">Demandes </span>Partenaires
          </TabsTrigger>
          <TabsTrigger value="internals" className="rounded-lg flex-1 min-w-[8rem] font-semibold flex items-center gap-2 text-xs md:text-sm">
            <AppWindow className="w-4 h-4" /> <span className="hidden sm:inline">Annonces </span>Internes
          </TabsTrigger>
          {commercialModeOn && (
            <TabsTrigger value="finances" className="rounded-lg flex-1 min-w-[8rem] font-semibold flex items-center gap-2 text-xs md:text-sm">
              <Banknote className="w-4 h-4" /> <span className="hidden sm:inline">Statistiques &amp; </span>Soldes
            </TabsTrigger>
          )}
        </TabsList>

        {/* --- ONGLET: PARTNAIRES (MODERATION) --- */}
        <TabsContent value="partners" className="mt-0 space-y-4">
          <Card className="border-border shadow-sm">
            <CardHeader className="border-b border-border/50">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-xl font-display">Modération des Annonces</CardTitle>
                  <CardDescription>Validez les propositions des partenaires et exportez les données.</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={exportToCSV} className="font-body text-xs border-green-200 text-green-700 hover:bg-green-50">
                  <Upload className="w-4 h-4 mr-2" /> Exporter (CSV)
                </Button>
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px] h-8 text-xs font-body"><SelectValue placeholder="Statut" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="approved">Approuvés</SelectItem>
                    <SelectItem value="rejected">Rejetés</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                  <SelectTrigger className="w-[160px] h-8 text-xs font-body"><SelectValue placeholder="Partenaire" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les partenaires</SelectItem>
                    {Array.from(new Set(pendingAds.map(a => a.organizerName))).map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={monthFilter} onValueChange={setMonthFilter}>
                  <SelectTrigger className="w-[140px] h-8 text-xs font-body"><SelectValue placeholder="Mois" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les mois</SelectItem>
                    {Array.from(new Set(pendingAds.map(a => {
                      const d = new Date(a.submittedAt);
                      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                    }))).map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filteredPendingAds.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">Aucune demande trouvée avec ces filtres.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 border-b border-border/50 text-muted-foreground">
                      <tr>
                        <th className="py-4 px-6 text-left">Organisation</th>
                        <th className="py-4 px-4 text-left">Visuel & Informations</th>
                        <th className="py-4 px-4 text-center">Durée / Lieu</th>
                        {commercialModeOn && <th className="py-4 px-4 text-center">Facturation</th>}
                        <th className="py-4 px-4 text-center">Statut</th>
                        <th className="py-4 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/30">
                      {filteredPendingAds.map(pad => (
                        <tr key={pad.id} className="hover:bg-muted/10 transition">
                          <td className="py-4 px-6">
                            <div className="font-bold text-sm">{pad.organizerName}</div>
                            <div className="text-[10px] text-muted-foreground mt-1">
                              Soumis le: {new Date(pad.submittedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                            </div>
                            {pad.isModified && <Badge className="bg-orange-500 animate-pulse mt-1">MODIFIÉE</Badge>}
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex gap-3 items-center">
                              {pad.imageUrl ? <img src={pad.imageUrl} className="w-12 h-8 object-cover rounded border border-border" /> : <div className="w-12 h-8 bg-muted rounded"></div>}
                              <div>
                                <h4 className="font-semibold text-xs truncate max-w-[150px]">{pad.title}</h4>
                                <a href={pad.linkUrl} target="_blank" className="text-[10px] text-muted-foreground underline">{pad.linkUrl || "Pas de lien"}</a>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <Badge variant="outline">{pad.duration} Jours</Badge>
                            <div className="text-[10px] text-muted-foreground mt-1 capitalize">{pad.location}</div>
                          </td>
                          {commercialModeOn && (
                            <td className="py-4 px-4 text-center">
                              <div className="font-bold text-foreground">{pad.cost} MAD</div>
                              {pad.paymentStatus === "paid" ? (
                                 <span className="text-[10px] text-emerald-600 font-bold flex items-center gap-1 justify-center"><CheckCircle className="w-3 h-3"/> Payé</span>
                              ) : (
                                 <span className="text-[10px] text-orange-600 flex items-center gap-1 justify-center">En attente</span>
                              )}
                            </td>
                          )}
                          <td className="py-4 px-4 text-center">
                            {pad.status === 'pending' && <Badge className="bg-orange-100 text-orange-700">À modérer</Badge>}
                            {pad.status === 'approved' && <Badge className="bg-emerald-100 text-emerald-700">Approuvé</Badge>}
                            {pad.status === 'rejected' && <Badge className="bg-destructive/10 text-destructive">Rejeté</Badge>}
                          </td>
                          <td className="py-4 px-6 text-right">
                             {pad.status === 'pending' && (
                               <div className="flex items-center justify-end gap-2">
                                 <Button size="sm" variant="outline" className="text-destructive px-2" onClick={() => handleRejectAd(pad.id)}><XCircle className="w-4 h-4"/></Button>
                                 <Button size="sm" onClick={() => handleApproveAd(pad)} className="bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5">
                                   <CreditCard className="w-4 h-4" /> {commercialModeOn ? "Facturer & Valider" : "Valider"}
                                 </Button>
                               </div>
                             )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- ONGLET: FINANCES & STATS --- */}
        {commercialModeOn && (
          <TabsContent value="finances" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
               <Card className="bg-primary/5 border-primary/20">
                 <CardContent className="p-6">
                   <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-2">Revenus Générés (Validés)</p>
                   <h2 className="text-4xl font-display font-bold text-foreground">{totalRevenues} MAD</h2>
                 </CardContent>
               </Card>
               <Card>
                 <CardContent className="p-6">
                   <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Partenaires Facturés</p>
                   <h2 className="text-4xl font-display font-bold text-foreground">{new Set(pendingAds.filter(a => a.status === 'approved').map(a => a.organizerName)).size}</h2>
                 </CardContent>
               </Card>
               <Card>
                 <CardContent className="p-6">
                   <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Annonces Diffusées</p>
                   <h2 className="text-4xl font-display font-bold text-foreground">{activePartnerAdsCount}</h2>
                 </CardContent>
               </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
               {/* Soldes Restants Partis */}
               <Card>
                 <CardHeader><CardTitle className="text-xl font-display">Soldes des Partenaires</CardTitle></CardHeader>
                 <CardContent>
                   <table className="w-full text-sm text-left">
                     <thead className="bg-muted"><tr><th className="p-3">Parti Politique</th><th className="p-3 text-right">Solde Actuel (MAD)</th></tr></thead>
                     <tbody className="divide-y">
                       {Object.entries(partnerBalances).map(([party, amount]) => (
                         <tr key={party}>
                           <td className="p-3 font-bold">{party}</td>
                           <td className="p-3 text-right">
                             <Badge variant={amount < 500 ? "destructive" : "secondary"}>{amount} MAD</Badge>
                           </td>
                         </tr>
                       ))}
                     </tbody>
                   </table>
                 </CardContent>
               </Card>
            </div>
          </TabsContent>
        )}

        {/* --- ONGLET: INTERNALS (Existing Ad Creation) --- */}
        <TabsContent value="internals" className="mt-0 space-y-6">
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setForm(initialForm); setEditingId(null); setShowForm(!showForm); }} className="font-body font-bold">
              <Plus className="w-4 h-4 mr-2" /> Créer une annonce système
            </Button>
          </div>
          
          {showForm && (
            <Card className="shadow-lg border border-primary/20 bg-card z-10 animate-in slide-in-from-top-4">
              <CardHeader className="bg-muted/10 border-b">
                <CardTitle className="text-lg font-display flex items-center gap-2">
                  {editingId ? <Edit2 className="w-5 h-5 text-primary" /> : <Plus className="w-5 h-5 text-primary" />}
                  {editingId ? "Modifier l'annonce interne" : "Nouvelle annonce système"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div><Label className="font-body text-primary font-bold">Nom interne (ex: popup ouverture)</Label><Input value={form.internalName || ''} onChange={e => setForm({ ...form, internalName: e.target.value })} className="mt-1" placeholder="Ex: Popup Accueil Législatives" /></div>
                   <div><Label className="font-body">Titre de l'annonce (Public)</Label><Input value={form.titleFr} onChange={e => setForm({ ...form, titleFr: e.target.value })} className="mt-1" placeholder="Titre affiché aux visiteurs" /></div>
                 </div>
                 <div><Label className="font-body">Description</Label><RichTextarea value={form.descriptionFr} onChange={e => setForm({ ...form, descriptionFr: e.target.value })} className="mt-1" rows={3} /></div>
                 
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border">
                   <div className="col-span-1">
                     <Label className="font-body mb-2 block">Upload Image</Label>
                     <div className="flex items-center gap-2">
                       <Input type="file" accept="image/*" onChange={handleImageUpload} className="w-1/2" />
                       <Input value={form.imageUrl || ''} onChange={e => setForm({ ...form, imageUrl: e.target.value })} placeholder="URL directe..." className="w-1/2" />
                     </div>
                   </div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-border">
                   <div>
                     <Label className="font-body mb-2 block">Positionnement *</Label>
                     <Select value={form.displayMode} onValueChange={(v: AdDisplayMode) => setForm({ ...form, displayMode: v })}>
                       <SelectTrigger><SelectValue placeholder="Mode" /></SelectTrigger>
                       <SelectContent>
                         <SelectItem value="sidebar">Sidebar</SelectItem>
                         <SelectItem value="banner">Banner (Haut)</SelectItem>
                         <SelectItem value="popup">Popup / Modale</SelectItem>
                         <SelectItem value="section_title_left">À côté d'un titre (Gauche)</SelectItem>
                         <SelectItem value="section_title_right">À côté d'un titre (Droite)</SelectItem>
                       </SelectContent>
                     </Select>
                   </div>
                   
                   {form.displayMode === "popup" && (
                     <div>
                       <Label className="font-body mb-2 block text-primary font-bold">Déclencheur du Popup *</Label>
                       <Select value={form.popupTrigger || "homepage"} onValueChange={(v: PopupTrigger) => setForm({ ...form, popupTrigger: v })}>
                         <SelectTrigger><SelectValue placeholder="Choix de déclenchement" /></SelectTrigger>
                         <SelectContent>
                           <SelectItem value="homepage">À l'ouverture (Page d'accueil uniquement)</SelectItem>
                           <SelectItem value="first_click">Au premier clic n'importe où</SelectItem>
                           <SelectItem value="always">Toujours (Sur toutes les pages)</SelectItem>
                           <SelectItem value="specific_page">Sur une page spécifique</SelectItem>
                         </SelectContent>
                       </Select>
                     </div>
                   )}
                   
                   {form.displayMode?.startsWith("section_title_") && (
                     <div>
                       <Label className="font-body mb-2 block">Section Cible *</Label>
                       <Select value={form.targetSection || "parties"} onValueChange={v => setForm({ ...form, targetSection: v })}>
                         <SelectTrigger><SelectValue placeholder="Choix de la section" /></SelectTrigger>
                         <SelectContent>
                           <SelectItem value="hero">Haut de page (Hero)</SelectItem>
                           <SelectItem value="parties">Partis Politiques</SelectItem>
                           <SelectItem value="events">Événements des partis</SelectItem>
                           <SelectItem value="barometers">Live Dashboard</SelectItem>
                           <SelectItem value="expectations">Parole aux Citoyens</SelectItem>
                         </SelectContent>
                       </Select>
                     </div>
                   )}
                   
                   <div>
                     <Label className="font-body mb-2 block">Couleur</Label>
                     <Select value={form.bgColor} onValueChange={v => setForm({ ...form, bgColor: v })}>
                       <SelectTrigger><SelectValue placeholder="Couleur..." /></SelectTrigger>
                       <SelectContent>
                         {colorOptions.map(c => (<SelectItem key={c.value} value={c.value}><div className="flex items-center gap-2"><div className={`w-3 h-3 rounded-full ${c.value}`}></div>{c.label}</div></SelectItem>))}
                       </SelectContent>
                     </Select>
                   </div>

                   <div>
                     <Label className="font-body mb-2 block">Lien URL</Label>
                     <Input value={form.linkUrl} onChange={e => setForm({ ...form, linkUrl: e.target.value })} placeholder="https://..." />
                   </div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border">
                   <div>
                     <Label className="font-body mb-2 block">Date de début (Optionnelle)</Label>
                     <Input type="datetime-local" value={form.startDate || ''} onChange={e => setForm({ ...form, startDate: e.target.value })} />
                   </div>
                   <div>
                     <Label className="font-body mb-2 block">Date de fin (Optionnelle)</Label>
                     <Input type="datetime-local" value={form.endDate || ''} onChange={e => setForm({ ...form, endDate: e.target.value })} />
                   </div>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-border">
                   <div>
                     <Label className="font-body mb-2 block">Taille du Popup</Label>
                     <Select value={form.popupSize || "md"} onValueChange={(v: any) => setForm({ ...form, popupSize: v })}>
                       <SelectTrigger><SelectValue placeholder="Taille..." /></SelectTrigger>
                       <SelectContent>
                         <SelectItem value="sm">Petit (Small)</SelectItem>
                         <SelectItem value="md">Moyen (Medium - Défaut)</SelectItem>
                         <SelectItem value="lg">Grand (Large)</SelectItem>
                         <SelectItem value="xl">Très Grand (Extra Large)</SelectItem>
                       </SelectContent>
                     </Select>
                   </div>
                   <div>
                     <Label className="font-body mb-2 block">Format du Texte (Description)</Label>
                     <Select value={form.textFormat || "normal"} onValueChange={(v: any) => setForm({ ...form, textFormat: v })}>
                       <SelectTrigger><SelectValue placeholder="Format..." /></SelectTrigger>
                       <SelectContent>
                         <SelectItem value="normal">Normal</SelectItem>
                         <SelectItem value="bold">Gras</SelectItem>
                         <SelectItem value="italic">Italique</SelectItem>
                         <SelectItem value="bold-italic">Gras & Italique</SelectItem>
                       </SelectContent>
                     </Select>
                   </div>
                 </div>

                 <div className="flex gap-4 justify-end pt-4 border-t mt-4 flex-wrap">
                   <Button variant="ghost" onClick={() => setShowForm(false)}>Annuler</Button>
                   <Button variant="outline" className="border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100" onClick={() => setPreviewOpen(true)} disabled={!form.titleFr && !form.imageUrl}>👁️ Prévisualiser</Button>
                   <Button variant="outline" className="border-orange-200 text-orange-600 bg-orange-50 hover:bg-orange-100" onClick={() => saveAd("draft")} disabled={!form.titleFr && !form.imageUrl}>Enregistrer le brouillon</Button>
                   <Button onClick={() => saveAd("published")} className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!form.titleFr && !form.imageUrl}>Publier l'annonce</Button>
                 </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {ads.map(ad => (
              <Card key={ad.id} className={`shadow-sm overflow-hidden border-l-4 ${ad.bgColor.replace('bg-', 'border-l-')}`}>
                <CardContent className="p-0 flex h-full">
                  <div className={`${ad.bgColor} p-4 w-1/3 flex flex-col justify-center text-white relative min-h-[140px]`} style={ad.imageUrl ? { backgroundImage: `url(${ad.imageUrl})`, backgroundSize: 'cover' } : {}}>
                     {ad.imageUrl && <div className="absolute inset-0 bg-black/40 z-0"></div>}
                     <div className="relative z-10">
                       <h4 className="font-display font-bold text-sm leading-tight mb-2 drop-shadow-md">{ad.titleFr}</h4>
                       <p className="text-[10px] opacity-90 line-clamp-3">{ad.descriptionFr}</p>
                     </div>
                  </div>
                  <div className="p-4 w-2/3 flex flex-col justify-between">
                    <div>
                      {ad.internalName && (
                        <h3 className="font-bold text-base text-primary mb-1">{ad.internalName}</h3>
                      )}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px]">{ad.displayMode?.toUpperCase()}</Badge>
                        {ad.displayMode?.startsWith("section_title_") && (
                          <Badge variant="secondary" className="text-[10px]">Cible: {ad.targetSection}</Badge>
                        )}
                        {ad.publishStatus === "draft" && (
                          <Badge className="bg-orange-100 text-orange-800 border-none text-[10px] font-bold">BROUILLON</Badge>
                        )}
                        <span className="text-[10px] text-muted-foreground mt-1 block w-full">ID: #{ad.id}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 justify-end mt-4">
                      {ad.publishStatus === "draft" && (
                        <Button variant="outline" size="sm" className="h-7 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50 mr-auto" onClick={() => {
                          setAds(ads.map(a => a.id === ad.id ? { ...a, publishStatus: "published" } : a));
                        }}>Publier</Button>
                      )}
                      <Badge variant={ad.active ? "default" : "secondary"} className="cursor-pointer" onClick={() => toggleActive(ad.id)}>
                        {ad.active ? "Active" : "Inactive"}
                      </Badge>
                      <Button variant="outline" size="sm" onClick={() => handleEditClick(ad)}><Edit2 className="w-4 h-4" /></Button>
                      <Button variant="outline" size="sm" className="text-destructive" onClick={() => handleDelete(ad.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {ads.length === 0 && <div className="col-span-2 text-center py-10 text-muted-foreground">Aucune campagne système.</div>}
          </div>
        </TabsContent>

      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className={`sm:max-w-${form.popupSize || 'md'} border-t-8 ${form.bgColor?.replace('bg-', 'border-t-')} z-[110] overflow-hidden`}>
          {form.imageUrl && (
             <div className="-mx-6 -mt-6 mb-4 relative">
               <img src={form.imageUrl} alt="Popup visual" className="w-full h-48 object-cover" />
             </div>
          )}
          <DialogHeader>
            {!form.imageUrl && (
              <div className={`mx-auto w-12 h-12 rounded-full ${form.bgColor} text-white flex items-center justify-center mb-4`}>
                <Megaphone className="w-6 h-6" />
              </div>
            )}
            {form.titleFr && <DialogTitle className="text-center font-display text-xl leading-tight text-balance">{form.titleFr}</DialogTitle>}
            {form.descriptionFr && (
              <DialogDescription 
                className={`text-center font-body mt-2 text-balance ${form.popupSize === 'lg' || form.popupSize === 'xl' ? 'text-base' : 'text-sm'} ${form.textFormat === 'bold' || form.textFormat === 'bold-italic' ? 'font-bold text-foreground' : ''} ${form.textFormat === 'italic' || form.textFormat === 'bold-italic' ? 'italic' : ''}`}
                dangerouslySetInnerHTML={{ __html: form.descriptionFr }}
              />
            )}
          </DialogHeader>
          {form.linkUrl && (
            <div className="flex justify-center mt-4">
              <Button asChild className={`${form.bgColor} text-white font-bold px-8 hover:opacity-90`}>
                <a href="#" onClick={(e) => { e.preventDefault(); setPreviewOpen(false); }}>Découvrir (Test)</a>
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAdsPage;
